/**
 * AddGameView - Unified page for adding games: search, bulk import, and platform sync
 */

import { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button, LoadingSpinner, ErrorMessage, DetailModal } from '@code-burrow/design-system';
import { useUsers, useUserView, deriveUserLabels, isCombinedView, SettingsPage, CollapsibleSection } from '@code-burrow/tracker-core';
import { GameStatus } from '../models/GameEntry.js';

const STATUS_OPTIONS = Object.values(GameStatus).map((s) => ({ value: s, label: s }));

/** Build ADD_GAME_FIELDS dynamically based on users */
function buildAddGameFields(users, activeView) {
    const labels = deriveUserLabels(users);
    const playedByOptions = [
        ...users.map((u) => ({ value: u.name, label: labels[u.name] || u.displayName })),
        { value: 'both', label: 'Both' },
    ];
    const defaultUser = isCombinedView(activeView) ? 'both' : (users.find((u) => u.name === activeView) ? activeView : users[0]?.name);
    return [
        { key: 'playedBy', label: 'Played By', type: 'select', options: playedByOptions, defaultValue: defaultUser },
        { key: 'status', label: 'Status', type: 'select', options: STATUS_OPTIONS },
        { key: 'rating', label: 'Rating', type: 'rating' },
        { key: 'lastPlayed', label: 'Last Played', type: 'date', defaultValue: 'today' },
    ];
}


function AddGameView({ backendService }) {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const { users } = useUsers();
    const { activeView } = useUserView();
    const labels = deriveUserLabels(users);
    const ADD_GAME_FIELDS = buildAddGameFields(users, activeView);
    const hasQuery = !!searchParams.get('q');
    const autoSearched = useRef(false);

    // Search state
    const [searchQuery, setSearchQuery] = useState(searchParams.get('q') || '');
    const [searchResults, setSearchResults] = useState([]);
    const [selectedGame, setSelectedGame] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(false);

    // Bulk import state
    const [steamIDs, setSteamIDs] = useState('');
    const [importing, setImporting] = useState(false);
    const [importProgress, setImportProgress] = useState({ current: 0, total: 0 });
    const [importResults, setImportResults] = useState(null);

    // Sync state
    const [syncPlatform, setSyncPlatform] = useState('steam');
    const [syncing, setSyncing] = useState(false);
    const [syncUser, setSyncUser] = useState(null);
    const [syncPlatformLabel, setSyncPlatformLabel] = useState(null);
    const [syncProgress, setSyncProgress] = useState({ current: 0, total: 0, title: '' });
    const [syncResults, setSyncResults] = useState(null);
    const [activeResultTab, setActiveResultTab] = useState(null);

    // Auto-search when navigated with ?q= param
    useEffect(() => {
        const q = searchParams.get('q');
        if (q && !autoSearched.current) {
            autoSearched.current = true;
            setSearchParams({}, { replace: true });
            runSearch(q);
        }
    }, [searchParams]);

    // ── Search handlers ──

    const runSearch = async (query) => {
        setError(null);
        setLoading(true);
        setSearchResults([]);

        try {
            const result = await backendService.searchGames(query);
            if (result.success && result.searchResults) {
                setSearchResults(result.searchResults.games || []);
                if (result.searchResults.games.length === 0) {
                    setError('No games found matching your search');
                }
            } else {
                setError(result.error || 'Search failed');
            }
        } catch (err) {
            setError(`Search failed: ${err.message}`);
        } finally {
            setLoading(false);
        }
    };

    const handleSearch = (e) => {
        e.preventDefault();
        runSearch(searchQuery);
    };

    const handleAddWithMetadata = async (_item, updates) => {
        const { playedBy, ...fieldUpdates } = updates;
        // First, create the game record (without user-specific fields)
        const result = await backendService.addGameFromData(selectedGame);

        if (!result.success) {
            throw new Error(result.error || 'Failed to add game');
        }

        // Then upsert user data via join table
        const gamePbId = result.pbId || result.entry?.id;
        if (gamePbId && Object.keys(fieldUpdates).length > 0) {
            const targetNames = playedBy === 'both'
                ? users.map((u) => u.name)
                : [playedBy || users[0]?.name || 'benny'];
            for (const userName of targetNames) {
                const linkResult = await backendService.upsertUserGame(userName, gamePbId, fieldUpdates);
                if (!linkResult.success) throw new Error(linkResult.error || 'Failed to save game details');
            }
        }
        setSuccess(true);
    };

    const handleAddAnother = () => {
        setSuccess(false);
        setSearchQuery('');
        setSearchResults([]);
        setSelectedGame(null);
        setError(null);
    };

    // ── Bulk import handlers ──

    const parseSteamIDs = (text) => {
        return text
            .split(/[\s,\n]+/)
            .map(id => id.trim())
            .filter(id => id.length > 0 && /^\d+$/.test(id));
    };

    const handleImport = async (e) => {
        e.preventDefault();
        setError(null);
        setImportResults(null);
        setImporting(true);

        const ids = parseSteamIDs(steamIDs);
        if (ids.length === 0) {
            setError('Please enter at least one valid Steam ID');
            setImporting(false);
            return;
        }

        setImportProgress({ current: 0, total: ids.length });
        const results = { successful: [], failed: [], skipped: [] };

        for (let i = 0; i < ids.length; i++) {
            const steamID = ids[i];
            setImportProgress({ current: i + 1, total: ids.length });

            try {
                const gameResult = await backendService.getGameByID(steamID);
                if (!gameResult.success) {
                    results.failed.push({ steamID, error: gameResult.error || 'Failed to fetch game data' });
                    continue;
                }

                const addResult = await backendService.addGameFromData(gameResult.gameData);
                if (addResult.success) {
                    // Link the game to the user (status + today's date) so it appears on the timeline
                    const gamePbId = addResult.pbId || addResult.entry?.id;
                    const targetUser = isCombinedView(activeView) ? users[0]?.name : activeView;
                    if (gamePbId && targetUser) {
                        const linkResult = await backendService.addToCollection(targetUser, gamePbId);
                        if (!linkResult.success) {
                            results.failed.push({ steamID, error: linkResult.error });
                            continue;
                        }
                    }
                    results.successful.push({ steamID, title: gameResult.gameData.title });
                } else if (addResult.error?.includes('already exists')) {
                    results.skipped.push({ steamID, title: gameResult.gameData.title, reason: 'Already in collection' });
                } else {
                    results.failed.push({ steamID, error: addResult.error || 'Failed to add to collection' });
                }
            } catch (err) {
                results.failed.push({ steamID, error: err.message });
            }

            if (i < ids.length - 1) {
                await new Promise(resolve => setTimeout(resolve, 500));
            }
        }

        setImportResults(results);
        setImporting(false);
    };

    const handleImportReset = () => {
        setSteamIDs('');
        setImportResults(null);
        setError(null);
        setImportProgress({ current: 0, total: 0 });
    };

    // ── Sync handlers ──

    const handleSteamSync = async (user) => {
        const userObj = users.find(u => u.name === user);
        const steamId = userObj?.steamId;
        if (!steamId) { setError(`Steam ID not configured for ${user}`); return; }
        setError(null);
        setSyncResults(null);
        setSyncing(true);
        setSyncUser(user);
        setSyncPlatformLabel('steam');
        setSyncProgress({ current: 0, total: 0, title: '' });

        try {
            const result = await backendService.syncFromSteam(steamId, user, (p) => setSyncProgress(p));
            if (result.success) setSyncResults(result);
            else setError(result.error || 'Sync failed');
        } catch (err) {
            setError(`Sync failed: ${err.message}`);
        } finally {
            setSyncing(false);
        }
    };

    const handleXboxSync = async (user) => {
        const userObj = users.find(u => u.name === user);
        const gamertag = userObj?.xboxGamertag;
        if (!gamertag) { setError(`Xbox gamertag not configured for ${user}`); return; }
        setError(null);
        setSyncResults(null);
        setSyncing(true);
        setSyncUser(user);
        setSyncPlatformLabel('xbox');
        setSyncProgress({ current: 0, total: 0, title: '' });

        try {
            const result = await backendService.syncFromXbox(gamertag, user, (p) => setSyncProgress(p));
            if (result.success) setSyncResults(result);
            else setError(result.error || 'Sync failed');
        } catch (err) {
            setError(`Sync failed: ${err.message}`);
        } finally {
            setSyncing(false);
        }
    };

    const handleEnrichXbox = async () => {
        setError(null);
        setSyncResults(null);
        setSyncing(true);
        setSyncUser(null);
        setSyncPlatformLabel('enrich');
        setSyncProgress({ current: 0, total: 0, title: '' });

        try {
            const result = await backendService.enrichXboxGames((p) => setSyncProgress(p));
            if (result.success) {
                setSyncResults({ added: result.enriched, updated: 0, skipped: result.skipped, failed: result.failed, errors: result.errors, addedList: result.enrichedList || [], updatedList: [], skippedList: result.skippedList || [] });
            } else {
                setError(result.error || 'Enrichment failed');
            }
        } catch (err) {
            setError(`Enrichment failed: ${err.message}`);
        } finally {
            setSyncing(false);
        }
    };

    const handleSyncReset = () => {
        setSyncResults(null);
        setError(null);
        setSyncUser(null);
        setSyncPlatformLabel(null);
        setSyncProgress({ current: 0, total: 0, title: '' });
    };

    // ── Success screen (search add) ──

    if (success) {
        return (
            <div className="bg-bg-primary min-h-screen flex items-center justify-center">
                <div className="bg-surface rounded-lg p-8 max-w-md text-center">
                    <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-success/10 mb-4">
                        <svg className="h-6 w-6 text-success" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                    </div>
                    <h3 className="font-body text-lg font-semibold text-text-primary mb-2">Game Added!</h3>
                    <div className="flex flex-col gap-3 mt-4">
                        <Button variant="primary" onClick={handleAddAnother}>Add Another</Button>
                        <Button variant="secondary" onClick={() => navigate('/')}>Go to Collection</Button>
                    </div>
                </div>
            </div>
        );
    }

    const platformLabelText = syncPlatformLabel === 'enrich' ? 'Xbox Enrich' : syncPlatformLabel === 'xbox' ? 'Xbox' : 'Steam';
    const syncUserObj = users.find(u => u.name === syncUser);
    const userEmoji = syncUserObj?.emoji || '';

    return (
        <SettingsPage>

                {/* Error Message */}
                {error && (
                    <div className="mb-4">
                        <ErrorMessage message={error} type="api" onDismiss={() => setError(null)} />
                    </div>
                )}

                {/* ── Bulk Import ── */}
                <CollapsibleSection
                    title="Bulk Import"
                    description="Import multiple games by Steam ID"
                    icon={<svg className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75V16.5m-13.5-9L12 3m0 0 4.5 4.5M12 3v13.5" /></svg>}
                >
                        {!importResults && (
                            <>
                                <form onSubmit={handleImport}>
                                    <div className="mb-4">
                                        <label htmlFor="steamIDs" className="block text-sm font-medium text-text-primary mb-2">
                                            Steam IDs
                                        </label>
                                        <textarea
                                            id="steamIDs"
                                            value={steamIDs}
                                            onChange={(e) => setSteamIDs(e.target.value)}
                                            placeholder={"Enter Steam IDs (one per line, or comma/space separated)\nExample:\n730\n440\n570"}
                                            rows={8}
                                            className="block w-full rounded-md border border-border bg-bg-primary text-text-primary px-3 py-2 focus:outline-none focus:border-accent-primary focus:ring-1 focus:ring-accent-primary text-sm font-mono"
                                            disabled={importing}
                                        />
                                        <p className="mt-2 text-sm text-text-tertiary">
                                            Enter Steam IDs separated by commas, spaces, or new lines.
                                        </p>
                                    </div>

                                    {steamIDs.trim() && (
                                        <div className="mb-4 p-3 bg-info-bg rounded-md border border-info/20">
                                            <p className="text-sm text-info">
                                                Ready to import {parseSteamIDs(steamIDs).length} game(s)
                                            </p>
                                        </div>
                                    )}

                                    <Button
                                        type="submit"
                                        variant="primary"
                                        disabled={!steamIDs.trim() || importing}
                                        loading={importing}
                                    >
                                        {importing ? 'Importing...' : 'Start Import'}
                                    </Button>
                                </form>

                                {importing && (
                                    <div className="mt-6">
                                        <div className="flex items-center justify-between mb-2">
                                            <span className="text-sm font-medium text-text-primary">Importing games...</span>
                                            <span className="text-sm text-text-secondary">
                                                {importProgress.current} / {importProgress.total}
                                            </span>
                                        </div>
                                        <div className="w-full bg-bg-tertiary rounded-full h-2">
                                            <div
                                                className="bg-accent-primary h-2 rounded-full transition-all duration-300"
                                                style={{ width: `${(importProgress.current / importProgress.total) * 100}%` }}
                                            />
                                        </div>
                                    </div>
                                )}
                            </>
                        )}

                        {importResults && (
                            <div className="space-y-4">
                                <div className="bg-bg-primary rounded-lg p-4">
                                    <h2 className="text-lg font-semibold text-text-primary mb-4">Import Complete</h2>
                                    <div className="grid grid-cols-3 gap-4">
                                        <div className="text-center p-4 bg-success-bg rounded-lg border border-success/20">
                                            <div className="text-2xl font-bold text-success">{importResults.successful.length}</div>
                                            <div className="text-sm text-success">Imported</div>
                                        </div>
                                        <div className="text-center p-4 bg-warning-bg rounded-lg border border-warning/20">
                                            <div className="text-2xl font-bold text-warning">{importResults.skipped.length}</div>
                                            <div className="text-sm text-warning">Skipped</div>
                                        </div>
                                        <div className="text-center p-4 bg-error-bg rounded-lg border border-error/20">
                                            <div className="text-2xl font-bold text-error">{importResults.failed.length}</div>
                                            <div className="text-sm text-error">Failed</div>
                                        </div>
                                    </div>
                                </div>

                                {importResults.successful.length > 0 && (
                                    <div className="bg-bg-primary rounded-lg p-4">
                                        <h3 className="text-sm font-semibold text-success mb-3">
                                            Successfully Imported ({importResults.successful.length})
                                        </h3>
                                        <div className="space-y-2 max-h-60 overflow-y-auto">
                                            {importResults.successful.map((item) => (
                                                <div key={item.steamID} className="flex items-center gap-2 text-sm">
                                                    <svg className="h-4 w-4 text-success flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                                    </svg>
                                                    <span className="text-text-secondary font-mono">{item.steamID}</span>
                                                    <span className="text-text-primary">{item.title}</span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {importResults.skipped.length > 0 && (
                                    <div className="bg-bg-primary rounded-lg p-4">
                                        <h3 className="text-sm font-semibold text-warning mb-3">
                                            Skipped ({importResults.skipped.length})
                                        </h3>
                                        <div className="space-y-2 max-h-60 overflow-y-auto">
                                            {importResults.skipped.map((item) => (
                                                <div key={item.steamID} className="flex items-center gap-2 text-sm">
                                                    <svg className="h-4 w-4 text-warning flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                                    </svg>
                                                    <span className="text-text-secondary font-mono">{item.steamID}</span>
                                                    <span className="text-text-primary">{item.title}</span>
                                                    <span className="text-text-tertiary">- {item.reason}</span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {importResults.failed.length > 0 && (
                                    <div className="bg-bg-primary rounded-lg p-4">
                                        <h3 className="text-sm font-semibold text-error mb-3">
                                            Failed ({importResults.failed.length})
                                        </h3>
                                        <div className="space-y-2 max-h-60 overflow-y-auto">
                                            {importResults.failed.map((item, index) => (
                                                <div key={`${item.steamID}-${index}`} className="flex items-start gap-2 text-sm">
                                                    <svg className="h-4 w-4 text-error flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                                    </svg>
                                                    <div className="flex-1">
                                                        <span className="text-text-secondary font-mono">{item.steamID}</span>
                                                        <span className="text-text-tertiary block">{item.error}</span>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                <div className="flex gap-3">
                                    <Button onClick={handleImportReset} variant="primary">Import More</Button>
                                    <Button onClick={() => navigate('/')} variant="secondary">View Collection</Button>
                                </div>
                            </div>
                        )}
                </CollapsibleSection>

                {/* ── Platform Sync ── */}
                <CollapsibleSection
                    title="Platform Sync"
                    description="Sync from Steam, Xbox, or Epic"
                    icon={<svg className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0 3.181 3.183a8.25 8.25 0 0 0 13.803-3.7M4.031 9.865a8.25 8.25 0 0 1 13.803-3.7l3.181 3.182" /></svg>}
                >
                        {!syncResults && !syncing && (
                            <>
                                <div className="flex gap-1 bg-surface rounded-lg p-1 border border-border-subtle w-fit">
                                    <button
                                        onClick={() => setSyncPlatform('steam')}
                                        className={`px-4 py-2 rounded-md text-sm font-medium transition-colors duration-150 ${
                                            syncPlatform === 'steam'
                                                ? 'bg-accent-primary text-text-inverse'
                                                : 'text-text-secondary hover:text-text-primary'
                                        }`}
                                    >
                                        Steam
                                    </button>
                                    <button
                                        onClick={() => setSyncPlatform('xbox')}
                                        className={`px-4 py-2 rounded-md text-sm font-medium transition-colors duration-150 ${
                                            syncPlatform === 'xbox'
                                                ? 'bg-[#107C10] text-white'
                                                : 'text-text-secondary hover:text-text-primary'
                                        }`}
                                    >
                                        Xbox
                                    </button>
                                </div>

                                {syncPlatform === 'steam' && (
                                    <div className="mt-3">
                                        <h2 className="font-body text-sm font-semibold text-text-primary mb-2">Steam Sync</h2>
                                        <p className="font-body text-sm text-text-secondary mb-6">
                                            Import games from your Steam library. New games with 10+ minutes of playtime
                                            will be added, and existing games will be updated with latest playtime data.
                                        </p>
                                        <div className="flex flex-col sm:flex-row gap-4">
                                            {users.map(u => (
                                                <UserSyncButton key={u.name} user={u.name} emoji={u.emoji} configured={!!u.steamId} statusText={u.steamId ? 'Ready to sync' : 'Steam ID not set'} onClick={() => handleSteamSync(u.name)} />
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {syncPlatform === 'xbox' && (
                                    <div className="mt-3">
                                        <h2 className="font-body text-sm font-semibold text-text-primary mb-2">Xbox Sync</h2>
                                        <p className="font-body text-sm text-text-secondary mb-4">
                                            Import games from your Xbox profile via OpenXBL. Games with achievement progress
                                            will be added. Existing games matched by title will be updated with last played dates.
                                        </p>
                                        <p className="font-body text-xs text-text-tertiary mb-6">
                                            Note: Xbox doesn't provide universal playtime stats. Last played dates and achievements are imported.
                                        </p>
                                        <div className="flex flex-col sm:flex-row gap-4">
                                            {users.map(u => (
                                                <UserSyncButton key={u.name} user={u.name} emoji={u.emoji} configured={!!u.xboxGamertag} statusText={u.xboxGamertag ? `GT: ${u.xboxGamertag}` : 'Gamertag not set'} onClick={() => handleXboxSync(u.name)} accentColor="bg-[#107C10]/10" />
                                            ))}
                                        </div>
                                        <div className="mt-4 pt-4 border-t border-border-subtle">
                                            <button
                                                onClick={handleEnrichXbox}
                                                className="text-sm font-medium text-text-secondary hover:text-accent-primary transition-colors duration-150"
                                            >
                                                Enrich Xbox games with RAWG metadata (genres, creator, image)
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </>
                        )}

                        {/* Progress */}
                        {syncing && (
                            <div className="mt-3">
                                <div className="flex items-center gap-3 mb-4">
                                    <div className="w-5 h-5 border-2 border-accent-primary border-t-transparent rounded-full animate-spin" />
                                    <h2 className="font-body text-sm font-semibold text-text-primary">
                                        {syncPlatformLabel === 'enrich'
                                            ? 'Enriching Xbox games with RAWG data...'
                                            : `Syncing ${userEmoji} ${syncUser}'s ${platformLabelText} library...`}
                                    </h2>
                                </div>
                                {syncProgress.total > 0 && (
                                    <>
                                        <div className="flex items-center justify-between mb-2">
                                            <span className="font-body text-sm text-text-secondary truncate mr-4">
                                                {syncProgress.title || 'Processing...'}
                                            </span>
                                            <span className="font-body text-sm text-text-secondary whitespace-nowrap">
                                                {syncProgress.current} / {syncProgress.total}
                                            </span>
                                        </div>
                                        <div className="w-full bg-bg-tertiary rounded-full h-2">
                                            <div
                                                className="bg-accent-primary h-2 rounded-full transition-all duration-300"
                                                style={{ width: `${(syncProgress.current / syncProgress.total) * 100}%` }}
                                            />
                                        </div>
                                    </>
                                )}
                            </div>
                        )}

                        {/* Results */}
                        {syncResults && (
                            <div className="space-y-4 mt-3">
                                <div>
                                    <h2 className="font-body text-sm font-semibold text-text-primary mb-4">
                                        {syncPlatformLabel === 'enrich'
                                            ? 'Xbox Enrichment Complete'
                                            : `${platformLabelText} Sync Complete ${userEmoji}`}
                                    </h2>
                                    <SyncResultTabs
                                        results={syncResults}
                                        activeTab={activeResultTab}
                                        onTabChange={setActiveResultTab}
                                        addedLabel={syncPlatformLabel === 'enrich' ? 'Enriched' : 'Added'}
                                    />
                                </div>

                                {syncResults.errors?.length > 0 && (
                                    <div className="bg-bg-primary rounded-lg p-4">
                                        <h3 className="font-body text-sm font-semibold text-error mb-3">
                                            Errors ({syncResults.errors.length})
                                        </h3>
                                        <div className="space-y-2 max-h-60 overflow-y-auto">
                                            {syncResults.errors.map((item, idx) => (
                                                <div key={`${item.steamID}-${idx}`} className="flex items-start gap-2 text-sm">
                                                    <svg className="h-4 w-4 text-error flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                                    </svg>
                                                    <div className="flex-1 min-w-0">
                                                        <span className="font-body text-text-primary">{item.title || item.steamID}</span>
                                                        <span className="font-body text-text-tertiary block truncate">{item.error}</span>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                <div className="flex gap-3">
                                    <Button onClick={handleSyncReset} variant="primary">Sync Again</Button>
                                    <Button onClick={() => navigate('/')} variant="secondary">View Collection</Button>
                                </div>
                            </div>
                        )}
                </CollapsibleSection>

            {/* Add preview modal (search) */}
            {selectedGame && (
                <DetailModal
                    mode="add"
                    item={selectedGame}
                    onClose={() => setSelectedGame(null)}
                    onSave={handleAddWithMetadata}
                    fields={ADD_GAME_FIELDS}
                    header={{
                        imageUrl: selectedGame.imageURL,
                        imageAlt: selectedGame.title,
                        title: selectedGame.title,
                        subtitle: selectedGame.creator,
                        tags: selectedGame.genres?.slice(0, 4),
                        extra: selectedGame.storeURL ? (
                            <a
                                href={selectedGame.storeURL}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="mt-2 inline-flex items-center gap-1 text-xs text-accent-primary hover:text-accent-hover transition-colors duration-150"
                            >
                                {selectedGame.source === 'rawg' ? 'View on RAWG' : 'View on Steam'}
                                <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                </svg>
                            </a>
                        ) : undefined,
                    }}
                />
            )}
        </SettingsPage>
    );
}

const RESULT_TABS = [
    { key: 'added', listKey: 'addedList',
      bg: 'bg-success-bg', border: 'border-success/20', activeBorder: 'border-success/40', ring: 'ring-success/30', hoverBorder: 'hover:border-success/40', text: 'text-success' },
    { key: 'updated', listKey: 'updatedList',
      bg: 'bg-info-bg', border: 'border-info/20', activeBorder: 'border-info/40', ring: 'ring-info/30', hoverBorder: 'hover:border-info/40', text: 'text-info' },
    { key: 'skipped', listKey: 'skippedList',
      bg: 'bg-warning-bg', border: 'border-warning/20', activeBorder: 'border-warning/40', ring: 'ring-warning/30', hoverBorder: 'hover:border-warning/40', text: 'text-warning' },
    { key: 'failed', listKey: null,
      bg: 'bg-error-bg', border: 'border-error/20', activeBorder: 'border-error/40', ring: 'ring-error/30', hoverBorder: 'hover:border-error/40', text: 'text-error' },
];

function SyncResultTabs({ results, activeTab, onTabChange, addedLabel = 'Added' }) {
    const labels = { added: addedLabel, updated: 'Updated', skipped: 'Skipped', failed: 'Failed' };
    const activeEntry = activeTab && RESULT_TABS.find(t => t.key === activeTab);
    const games = activeEntry?.listKey ? (results[activeEntry.listKey] || []) : [];
    const sorted = [...games].sort((a, b) => a.localeCompare(b));

    return (
        <div>
            <div className="grid grid-cols-4 gap-2">
                {RESULT_TABS.map((tab) => {
                    const count = results[tab.key] ?? 0;
                    const hasList = tab.listKey && results[tab.listKey]?.length > 0;
                    const isActive = activeTab === tab.key;
                    return (
                        <button
                            key={tab.key}
                            onClick={() => hasList ? onTabChange(isActive ? null : tab.key) : null}
                            className={`p-3 rounded-lg border transition-all duration-150 ${tab.bg} ${
                                isActive
                                    ? `${tab.activeBorder} ring-1 ${tab.ring}`
                                    : `${tab.border} ${hasList ? `${tab.hoverBorder} cursor-pointer` : 'cursor-default'}`
                            }`}
                        >
                            <div className={`text-2xl font-bold ${tab.text}`}>{count}</div>
                            <div className={`text-sm ${tab.text}`}>{labels[tab.key]}</div>
                        </button>
                    );
                })}
            </div>
            {activeTab && sorted.length > 0 && (
                <div className={`mt-3 p-4 rounded-lg ${activeEntry.bg} border ${activeEntry.border}`}>
                    <ul className="columns-2 sm:columns-3 gap-x-4 space-y-0.5">
                        {sorted.map((name, idx) => (
                            <li key={idx} className={`text-sm ${activeEntry.text} truncate`}>{name}</li>
                        ))}
                    </ul>
                </div>
            )}
        </div>
    );
}

function UserSyncButton({ user, emoji, configured, statusText, onClick, accentColor = 'bg-accent-primary/10' }) {
    return (
        <button
            onClick={onClick}
            disabled={!configured}
            className="flex-1 flex items-center gap-4 p-4 bg-bg-primary border border-border rounded-lg hover:bg-surface-hover hover:border-accent-primary transition-colors duration-150 disabled:opacity-50 disabled:cursor-not-allowed"
        >
            <div className={`w-12 h-12 ${accentColor} rounded-full flex items-center justify-center text-2xl`}>
                {emoji}
            </div>
            <div className="text-left">
                <p className="font-body text-sm font-semibold text-text-primary capitalize">{user}</p>
                <p className="font-body text-xs text-text-secondary">{statusText}</p>
            </div>
        </button>
    );
}

export default AddGameView;
